import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { 
  useZoneQuery, 
  useDeleteZoneMutation 
} from "@/api/v0/zones";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { toast } from "sonner";
import { Map } from "react-map-gl/maplibre";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Edit, Loader2, Trash2 } from "lucide-react";
import { Source, Layer } from "react-map-gl/maplibre";

export function ZoneDetailRoute() {
  const navigate = useNavigate();
  const params = useParams({ from: "/zones/$zoneId" });
  const zoneId = params.zoneId;
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const { data: zone, isLoading, isError } = useZoneQuery(zoneId);
  const deleteMutation = useDeleteZoneMutation();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(zoneId);
      toast.success("Zone deleted successfully");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ to: "/zones" as any });
    } catch {
      toast.error("Failed to delete zone");
    } finally {
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !zone) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-destructive mb-4">Failed to load zone</p>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Button onClick={() => navigate({ to: "/zones" as any })}>
          Back to Zones
        </Button>
      </div>
    );
  }

  // Create GeoJSON for the zone
  const geoJson = {
    type: "Feature",
    properties: {},
    geometry: zone.geometry.type === "polygon" 
      ? {
          type: "Polygon",
          coordinates: zone.geometry.coordinates
        }
      : {
          type: "Point",
          coordinates: zone.geometry.center
        }
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="icon" 
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick={() => navigate({ to: "/zones" as any })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">{zone.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick={() => navigate({ to: `/zones/${zoneId}/edit` as any })}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Zone</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this zone? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Zone Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Type</h3>
                <p className="capitalize">{zone.type}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Color</h3>
                <div className="flex items-center gap-2 mt-1">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: zone.color }}
                  />
                  <span>{zone.color}</span>
                </div>
              </div>
              
              {zone.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
                  <p>{zone.description}</p>
                </div>
              )}
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Created</h3>
                <p>{new Date(zone.createdAt).toLocaleString()}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Last Updated</h3>
                <p>{new Date(zone.updatedAt).toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Zone Map</CardTitle>
              <CardDescription>
                Visual representation of the zone
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[400px] w-full">
                <Map
                  mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                  initialViewState={{
                    longitude: zone.geometry.type === "polygon" 
                      ? zone.geometry.coordinates[0][0][0] 
                      : zone.geometry.center[0],
                    latitude: zone.geometry.type === "polygon" 
                      ? zone.geometry.coordinates[0][0][1] 
                      : zone.geometry.center[1],
                    zoom: 12
                  }}
                  style={{ width: "100%", height: "100%" }}
                  mapLib={import('maplibre-gl')}
                  attributionControl={false}
                  reuseMaps
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  <Source id="zone-source" type="geojson" data={geoJson as any}>
                    <Layer
                      id="zone-fill"
                      type="fill"
                      paint={{
                        "fill-color": zone.color,
                        "fill-opacity": 0.3,
                      }}
                    />
                    <Layer
                      id="zone-outline"
                      type="line"
                      paint={{
                        "line-color": zone.color,
                        "line-width": 2,
                      }}
                    />
                  </Source>
                </Map>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 